import Layout from 'antd/lib/layout'
import BackTop from 'antd/lib/back-top'
import React, { useState, useEffect, Suspense } from 'react'
import { Route, Routes, useLocation, Outlet } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import Sidebar from './Sidebar'
import TermsAndConditions from './TermsAndConditions'
import PrintOrder from 'components/Orders/PrintOrder'
import MarketingInitiativeModal from '../MarketingAndSales/InitiativeModal'
import { useSession, isAdmin, User } from '@slerp/accounts'
// import UserFlow from './../Utils/Userflow'
import { useHubspotChat } from '../../hooks/useHubspotChat'
import { CREATE_MERCHANT_SUBSCRIPTION } from './actions'
// import moment from 'moment'
import { StampLogs } from './StampLogs'
import { usePageViewTracker } from 'packages/@slerp/tracker'
import Loading from 'components/Utils/Loading'
import { useGlobalQuery } from 'hooks/useGlobalQuery'
import LayoutTitle from './LayoutTitle'
import LayoutContent from './LayoutContent'
import styled from '@emotion/styled'
import { ExpiredCardWarning } from '../Common'
import { COLLAPSED_MARGIN_LEFT, EXPANDED_MARGIN_LEFT } from './constants'
import { getPageTitle } from './utils'
import { useMerchant } from 'hooks/useGlobalContext/useMerchant'
import isEmpty from 'lodash/isEmpty'

export interface PageHeaderType {
  title: string
  tooltipTitle?: React.ReactNode
}

export interface CardType {
  id: string
  last4: number
  expMonth: number
  expYear: number
  expired?: boolean
  defaultSource: string
}

const AppContainer = () => {
  usePageViewTracker()
  const { merchant, user } = useSession()
  const {
    hasExpiredDefaultCard,
    // onboardingStatus,
    collapsed
  } = useGlobalQuery()
  const { merchantData } = useMerchant()
  const [pageHeader, setPageHeader] = useState<PageHeaderType | null>(null)
  const [displayMarketingModal, setDisplayMarketingModal] =
    useState<boolean>(false)
  const location = useLocation()
  const pathContext = location?.pathname?.split('/').slice(1, 2)[0]
  const isPrintOrder = pathContext === 'print-order'

  // const { initUserFlow, userflow, safeOpenResourceCenter } = UserFlow()
  const hubspotJWT = useHubspotChat(user as User)

  const [createSubscription] = useMutation(CREATE_MERCHANT_SUBSCRIPTION, {
    fetchPolicy: 'no-cache'
  })

  // const isOnboarding = merchantSetting?.has_onboarded
  // const isIdentified = userflow.isIdentified()

  // Separate Userflow initialization (no dependency on Hubspot)
  // useEffect(() => {
  //   if (isPrintOrder) return
  //   if (!merchantSetting) return

  //   initUserFlow({
  //     user: user as User,
  //     hasOnboarded: isOnboarding,
  //     onboardingStatus: onboardingStatus,
  //     isAdmin: isAdmin(user),
  //     companyName: merchantSetting.name
  //   })
  // }, [onboardingStatus, isOnboarding, user?.inserted_at, merchantSetting])

  // Separate Hubspot chat initialization
  useEffect(() => {
    if (isPrintOrder) return
    if (!hubspotJWT) return

    const identifyAndLoad = () => {
      ;(window as any).hsConversationsSettings = {
        loadImmediately: false,
        identificationEmail: user?.email,
        identificationToken: hubspotJWT
      }

      // If already loaded from a prior run, reset and remove before reloading

      if ((window as any).HubSpotConversations?.widget?.isLoaded?.()) {
        ;(window as any).HubSpotConversations.clear?.({ resetWidget: true })
        ;(window as any).HubSpotConversations.widget.remove?.()
      }

      ;(window as any).HubSpotConversations?.widget?.load?.()
    }

    if ((window as any).HubSpotConversations) {
      identifyAndLoad()
    } else {
      const t = setInterval(() => {
        if ((window as any).HubSpotConversations) {
          clearInterval(t)
          identifyAndLoad()
        }
      }, 200)
      return () => clearInterval(t)
    }
  }, [hubspotJWT, user?.email])

  // useEffect(() => {
  //   if (isPrintOrder) return // Skip Resource Center for print orders
  //   if (!isIdentified) return
  //   const userInsertedAt = moment(user?.inserted_at)
  //   const fourteenDaysAgo = moment().subtract(14, 'days')
  //   const timer = setTimeout(() => {
  //     if (userInsertedAt.isAfter(fourteenDaysAgo)) {
  //       safeOpenResourceCenter()
  //     }
  //   }, 7500)

  //   return () => clearTimeout(timer)
  // }, [isIdentified, isPrintOrder])

  useEffect(() => {
    createSubscription({
      variables: {
        merchant_id: merchant?.id
      }
    }).catch((error) =>
      console.error(`Unable to create subscription due to ${error}`, 5)
    )
  }, [])

  useEffect(() => {
    if (isEmpty(merchantData)) {
      return
    }

    const { marketing_modal_viewers } = merchantData.setting
    setDisplayMarketingModal(
      !(marketing_modal_viewers || []).includes(user?.id || '')
    )
  }, [merchantData])

  useEffect(() => {
    const { documentTitle, title, tooltipTitle } = getPageTitle(
      pathContext,
      location.pathname
    )

    document.title = documentTitle
    setPageHeader({ title, tooltipTitle })
  }, [location.pathname])

  if (isEmpty(merchantData)) {
    return <Loading />
  }

  if (pathContext === 'print-order')
    return (
      <Routes>
        <Route path='/print-order/:order_id' element={<PrintOrder />} />
      </Routes>
    )

  return (
    <StyledLayout hasSider collapsed={collapsed}>
      <Sidebar />
      <StampLogs />
      <Layout className='site-layout'>
        <LayoutTitle pageHeader={pageHeader} />

        {displayMarketingModal &&
          (location.pathname === '/' || location.pathname === '/analytics') && (
            <MarketingInitiativeModal />
          )}
        <ExpiredCardWarning show={!!hasExpiredDefaultCard} />
        <LayoutContent>
          <Suspense fallback={<Loading />}>
            <Outlet />
          </Suspense>
          <BackTop />
        </LayoutContent>
        <TermsAndConditions
          isAdmin={isAdmin(user)}
          merchantId={merchant?.id || ''}
          merchantName={merchant?.name || ''}
          registeredCompanyName={merchantData?.registered_company_name}
        />
      </Layout>
    </StyledLayout>
  )
}

const StyledLayout = styled(Layout)<{ collapsed: boolean }>`
  .ant-btn:not(.ant-btn-icon-only),
  .ant-btn.ant-btn-default:not(.ant-btn-icon-only) {
    display: flex;
    align-items: center;
  }

  .site-layout {
    margin-left: ${({ collapsed }) =>
      collapsed ? `${COLLAPSED_MARGIN_LEFT}px` : `${EXPANDED_MARGIN_LEFT}px`};
    transition: margin-left 0.2s;
  }
`

export default AppContainer
